import { Grid } from '@mui/material';
import { Checkbox, RoutalSelect, RoutalSelectMultiSelectOption, VSpacer } from '@nx-smartmonkey/ui';
import { get } from 'lodash';

import { useEffect, useState } from 'react';
import { RoundingMinutesSlider } from '../../../../components/RoundingMinutesSlider';
import { Project } from '../../../../domain/project/Project';
import {
  ProjectCustomersAppType,
  ProjectCustomersFieldTypes,
  projectCustomersAppValues,
} from '../../../../domain/project/projectCustomers/ProjectCustomersFields';
import { StatusHistoryTypes, customersStopStatus } from '../../../../domain/project/projectCustomers/StatusHistory';
import { TimeOptionsMinutes } from '../../../../domain/project/projectCustomers/TimeOptions';
import { intl } from '../../../../helpers/IntGlobalProvider';
import { useUpdateProject } from '../../../../hooks/project/useUpdateProject';
import ProjectCustomersMapper from '../../../../mappers/project/ProjectCustomersMapper';
import { Accordion } from '../../../../react-components/Accordion';
import InputText from '../../../../react-components/InputText';
import Text from '../../../../react-components/Text';
import { CheckboxOptionPill } from './pills/CheckboxOptionPill';

const customersFields = [
  ...Object.keys(projectCustomersAppValues).filter(
    (fieldKey) =>
      ![
        `pin`,
        `phone_call`,
        `additional_info`,
        `customer_text_field`,
        `survey`,
        `stop_position`,
        `driver_position`,
        `status_history`,
      ].includes(fieldKey)
  ),
] as ProjectCustomersAppType[];

interface CustomersAppMonitorProps {
  project: Project;
}

export const CustomersAppMonitor = ({ project }: CustomersAppMonitorProps) => {
  const { run: editProject } = useUpdateProject({ project });
  const [statusTexts, setStatusTexts] = useState<Record<StatusHistoryTypes, string>>({
    created: ``,
    scheduled: ``,
    on_route: ``,
    completed: ``,
    canceled: ``,
  });

  useEffect(() => {
    const statusHistoryField = project.customers.fields.find((field) => field.id === `status_history`);

    const temporalStatus = customersStopStatus.reduce((amount, status) => {
      const statusField = statusHistoryField?.status_history?.find((s) => s.id === status);
      if (statusField) {
        return { ...amount, [status]: statusField.label };
      }
      return amount;
    }, statusTexts);

    setStatusTexts(temporalStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.customers.fields]);

  const isTimeOptionEnabled = (project: Project, option: ProjectCustomersAppType): boolean => {
    return (
      project.customers.fields.some((field) => field.id === option) &&
      [`estimated_arrival_time`, `planned_arrival_time`].includes(option)
    );
  };

  const getRoundingMinutes = (project: Project, option: ProjectCustomersAppType): TimeOptionsMinutes => {
    const fieldFound = project.customers.fields.find((field) => field.id === option);
    const roundingMinutes = get(fieldFound, `time_options.rounding_minutes`, 0);
    return roundingMinutes;
  };

  const getRoundingBounds = (project: Project, option: ProjectCustomersAppType): number[] | undefined => {
    const fieldFound = project.customers.fields.find((field) => field.id === option);
    if (fieldFound?.time_options?.left_bound && fieldFound?.time_options?.right_bound) {
      return [fieldFound.time_options.left_bound, fieldFound.time_options.right_bound];
    }
    return undefined;
  };

  const getHumanRoundingMinutes = (minutes: TimeOptionsMinutes): string => {
    return minutes === 0
      ? intl.formatMessage({ id: `customers.app.options.monitor.rounding.exact_time` })
      : `${minutes} min`;
  };

  return (
    <div
      style={{
        border: `1px solid #E0E0E0`,
        borderRadius: `2px`,
      }}
    >
      <Accordion
        id="customers_app_monitor"
        expanded
        headerStyles={{
          position: `relative`,
          paddingTop: `16px`,
        }}
        headerContent={
          <Text style={{ cursor: `pointer` }} variant="body-large" isBold intlId="customers.app.options.monitor" />
        }
        contentStyle={{
          padding: `16px`,
          borderTop: `1px solid #E0E0E0`,
        }}
      >
        <Text variant="label" intlId="customers.app.options.monitor.info" />

        <VSpacer small />

        <CheckboxOptionPill
          showCheckbox={false}
          text={intl.formatMessage({ id: `customers.app.options.monitor.map` })}
          style={{
            marginBottom: 0,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          }}
        >
          <Grid
            container
            direction="column"
            style={{
              border: `1px solid #E0E0E0`,
              borderTop: 0,
              borderBottomLeftRadius: `2px`,
              borderBottomRightRadius: `2px`,
              padding: `8px`,
              marginBottom: `4px`,
            }}
            wrap="nowrap"
          >
            {([`driver_position`, `stop_position`] as ProjectCustomersAppType[]).map((mapOption, index) => {
              const selected = project.customers.fields.some((field) => field.id === mapOption);
              return (
                <>
                  {index !== 0 ? <VSpacer medium /> : null}

                  <Grid item container direction="row" alignItems="center" justifyContent="space-between">
                    <Grid item>
                      <Text
                        variant={!selected ? `label` : `body`}
                        intlId={`customers.app.options.monitor.${mapOption}`}
                      />
                    </Grid>

                    <Grid item>
                      <Checkbox
                        checked={selected}
                        onChange={(e) => {
                          const projectCustomers = ProjectCustomersMapper.toDTO(project.customers);

                          projectCustomers.fields = e.target.checked
                            ? [
                                ...project.customers.fields,
                                {
                                  id: mapOption,
                                  type: projectCustomersAppValues[mapOption] as ProjectCustomersFieldTypes,
                                },
                              ]
                            : project.customers.fields.filter((field) => field.id !== mapOption);

                          editProject({
                            customers: ProjectCustomersMapper.toDomain(projectCustomers),
                          });
                        }}
                      />
                    </Grid>
                  </Grid>
                </>
              );
            })}
          </Grid>
        </CheckboxOptionPill>

        <CheckboxOptionPill
          text={intl.formatMessage({ id: `customers.app.options.monitor.status_history` })}
          info={intl.formatMessage({ id: `customers.app.options.monitor.status_history.info` })}
          selected={project.customers.fields.some((field) => field.id === `status_history`)}
          onSelect={(selected: boolean) => {
            const projectCustomers = ProjectCustomersMapper.toDTO(project.customers);

            projectCustomers.fields = selected
              ? [
                  ...project.customers.fields,
                  {
                    id: `status_history`,
                    type: projectCustomersAppValues[`status_history`] as ProjectCustomersFieldTypes,
                  },
                ]
              : project.customers.fields.filter((field) => field.id !== `status_history`);

            setStatusTexts({
              created: ``,
              scheduled: ``,
              on_route: ``,
              completed: ``,
              canceled: ``,
            });

            editProject({
              customers: ProjectCustomersMapper.toDomain(projectCustomers),
            });
          }}
          style={{
            marginBottom: 0,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          }}
        >
          <Grid
            container
            direction="column"
            style={{
              border: `1px solid #E0E0E0`,
              borderTop: 0,
              borderBottomLeftRadius: `2px`,
              borderBottomRightRadius: `2px`,
              padding: `8px`,
              marginBottom: `4px`,
            }}
            wrap="nowrap"
          >
            {customersStopStatus.map((status, index) => {
              const statusHistoryField = project.customers.fields.find((field) => field.id === `status_history`);
              return (
                <>
                  {index !== 0 ? <VSpacer medium /> : null}

                  <Grid item container direction="column">
                    <Grid item>
                      <Text variant="label" intlId={`customers.app.options.monitor.status_history.${status}`} />
                    </Grid>

                    <VSpacer small />

                    <Grid item>
                      <InputText
                        disabled={!project.customers.fields.some((field) => field.id === `status_history`)}
                        inputContainerStyles={{ width: `100%` }}
                        placeholderIntlId={`customers.app.options.monitor.status_history.${status}.ph`}
                        fieldValue={
                          (statusHistoryField?.status_history ?? []).find((s) => s.id === status)?.label ?? ``
                        }
                        onChange={(value: string) => {
                          setStatusTexts({
                            ...statusTexts,
                            [status]: value,
                          });
                        }}
                        onBlur={(e: any) => {
                          const projectCustomers = ProjectCustomersMapper.toDTO(project.customers);

                          const currentStatusHistoryFields = statusHistoryField?.status_history ?? [];

                          const newStatusFields = [
                            ...currentStatusHistoryFields.filter(({ id }) => id !== status),
                            { id: status, label: statusTexts[status] },
                          ];

                          const areEqual = newStatusFields.every((field) => {
                            return currentStatusHistoryFields.find(({ id }) => id === field.id)?.label === field.label;
                          });

                          if (!areEqual) {
                            projectCustomers.fields = projectCustomers.fields.map((field) => {
                              if (field.id === `status_history`) {
                                field.status_history = newStatusFields;
                              }

                              return field;
                            });

                            editProject({
                              customers: ProjectCustomersMapper.toDomain(projectCustomers),
                            });
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </>
              );
            })}
          </Grid>
        </CheckboxOptionPill>

        {customersFields.map((option) => {
          return (
            <CheckboxOptionPill
              text={intl.formatMessage({ id: `customers.app.options.monitor.${option}` })}
              info={intl.formatMessage({ id: `customers.app.options.monitor.${option}.info` })}
              selected={project.customers.fields.some((field) => field.id === option)}
              onSelect={(selected: boolean) => {
                const projectCustomers = ProjectCustomersMapper.toDTO(project.customers);

                projectCustomers.fields = selected
                  ? [
                      ...project.customers.fields,
                      { id: option, type: projectCustomersAppValues[option] as ProjectCustomersFieldTypes },
                    ]
                  : project.customers.fields.filter((field) => field.id !== option);

                editProject({
                  customers: ProjectCustomersMapper.toDomain(projectCustomers),
                });
              }}
              style={
                isTimeOptionEnabled(project, option)
                  ? {
                      marginBottom: 0,
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0,
                    }
                  : {}
              }
            >
              {isTimeOptionEnabled(project, option) ? (
                <Grid
                  container
                  direction="column"
                  style={{
                    border: `1px solid #E0E0E0`,
                    borderTop: 0,
                    borderBottomLeftRadius: `2px`,
                    borderBottomRightRadius: `2px`,
                    padding: `8px`,
                    marginBottom: `4px`,
                  }}
                  wrap="nowrap"
                >
                  <Grid item>
                    <Text variant="label" intlId="customers.app.options.monitor.rounding" />
                  </Grid>

                  <VSpacer small />

                  <Grid item container direction="row" alignItems="center" justifyContent="space-between">
                    <Text intlId="customers.app.options.monitor.rounding.select_rounding" />

                    <RoutalSelect
                      id="customers-application-select-minutes"
                      containerStyle={{
                        width: `80px`,
                      }}
                      style={{
                        justifyContent: `space-between`,
                      }}
                      popoverProps={{
                        options: ([0, 15, 30, 60] as TimeOptionsMinutes[]).map(
                          (minutes): RoutalSelectMultiSelectOption => ({
                            key: `time-${minutes}`,
                            content: <Text>{getHumanRoundingMinutes(minutes)}</Text>,
                            onClick: () => {
                              const projectCustomers = ProjectCustomersMapper.toDTO(project.customers);

                              projectCustomers.fields = projectCustomers.fields.map((field) => {
                                if (field.id === option) {
                                  field.time_options =
                                    minutes !== 0
                                      ? {
                                          rounding_minutes: minutes,
                                          left_bound: -minutes,
                                          right_bound: minutes,
                                        }
                                      : undefined;
                                }

                                return field;
                              });

                              editProject({
                                customers: ProjectCustomersMapper.toDomain(projectCustomers),
                              });
                            },
                          })
                        ),
                      }}
                    >
                      <Text>{getHumanRoundingMinutes(getRoundingMinutes(project, option))}</Text>
                    </RoutalSelect>
                  </Grid>

                  <VSpacer small />

                  {getRoundingMinutes(project, option) !== 0 ? (
                    <Grid container direction="column">
                      <Grid item>
                        <Text variant="label" intlId="customers.app.options.monitor.rounding.select_interval" />
                      </Grid>

                      <VSpacer small />

                      <RoundingMinutesSlider
                        defaultBounds={getRoundingBounds(project, option)}
                        roundingMinutes={getRoundingMinutes(project, option)}
                        onChange={(value: number[]) => {
                          const projectCustomers = ProjectCustomersMapper.toDTO(project.customers);

                          projectCustomers.fields = projectCustomers.fields.map((field) => {
                            if (field.id === option) {
                              field.time_options!.left_bound = value[0];
                              field.time_options!.right_bound = value[1];
                            }
                            return field;
                          });

                          editProject({
                            customers: ProjectCustomersMapper.toDomain(projectCustomers),
                          });
                        }}
                      />

                      {/* <VSpacer medium />

                      <Grid item>
                        <Text variant="label" intlId="customers.app.options.monitor.rounding.example" />

                        <VSpacer small />

                        <Text
                          intlId="customers.app.options.monitor.rounding.example_result"
                          intlValues={{ start: 100, end: 200 }}
                        />
                      </Grid> */}

                      <VSpacer small />
                    </Grid>
                  ) : null}
                </Grid>
              ) : null}
            </CheckboxOptionPill>
          );
        })}
      </Accordion>
    </div>
  );
};
